import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  picklistTemplateListPageQuery,
  picklistTemplateDelete as picklistTemplateDeleteMutation,
} from '../graphql/picklist_template_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const PicklistTemplateList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [picklistTemplateDelete] = useMutation(picklistTemplateDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(picklistTemplateListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ picklistTemplateList }) => {
      // write an individual query for every picklistTemplate in the list
      updateFunctions.picklistTemplateList(pageClient, picklistTemplateList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const picklistTemplateDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const picklistTemplateId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: picklistTemplateId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'PicklistTemplateType',
        recordId: picklistTemplateId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.picklistTemplateDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimisticNew({
      mutationName: 'picklistTemplateDelete',
      mutationData,
    });
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await picklistTemplateDelete(mutationData);
        toastSuccess('Picklist Template delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Picklist Template delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      picklistTemplateDelete(mutationData);
      toastWarning(`Picklist Template delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const renderPicklistTemplateProductsCell = (cell) => {
    const items = cell.getValue();
    return (
      <span>
        {items.map((item) => {
          const {
            id,
            product: { name },
          } = item;
          return (
            <span key={id}>
              {`${name}`}
              <br />
            </span>
          );
        })}
      </span>
    );
  };

  const parentColumns = [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Product Assemblies',
      accessorKey: 'picklistTemplateProducts',
      cell: renderPicklistTemplateProductsCell,
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Picklist Templates</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/picklist_templates/new">
                <Button variant="primary">New Picklist Template</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="picklistTemplate"
                parentColumns={parentColumns}
                data={pageData.picklistTemplateList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={picklistTemplateDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default PicklistTemplateList;
